import { useCallback, useMemo } from 'react';

import { UserRole } from '@/Enums/enum';

import { useCurrentUser } from './useAuth';

/**
 * Centralized permission system for role-based access control.
 *
 * Roles:
 * - admin: Full access to all features and courses
 * - dept: Department chair, can see their courses + courses of users they supervise
 * - user: Can only see/edit their assigned courses
 */
export const usePermissions = () => {
	const { data: currentUser, isLoading } = useCurrentUser();

	const userRole = currentUser?.user_role as UserRole | undefined;

	// Role checks
	const isAdmin = userRole === UserRole.ADMIN;
	const isDeptChair = userRole === UserRole.DEPT_CHAIR;
	const isUser = userRole === UserRole.USER;

	// Feature permissions
	const permissions = useMemo(
		() => ({
			// Admin-only features
			canManageUsers: isAdmin,
			canManageTags: isAdmin,
			canManageDisciplines: isAdmin,
			canManageDivisions: isAdmin,
			canViewStats: isAdmin,
			canManageSettings: isAdmin,
			canViewCourseMap: isAdmin,

			// Admin and Dept Chair features
			canApproveSubmissions: isAdmin || isDeptChair,

			// Common features (all authenticated users)
			canViewCourses: true,
			canViewSubmissions: true,
			canViewCourseAlignment: true,
		}),
		[isAdmin, isDeptChair]
	);

	/**
	 * Check if the current user can edit a specific course.
	 * A user can edit a course if:
	 * - They are an admin
	 * - They are assigned to the course
	 * - They are the department chair of someone assigned to the course
	 */
	const canEditCourse = useCallback(
		(courseUsers: CourseUserEntity[] | undefined): boolean => {
			if (!currentUser || !courseUsers) return false;

			// Admins can edit all courses
			if (isAdmin) return true;

			// Check if user is directly assigned to the course
			const isAssigned = courseUsers.some(
				(cu) => cu.user_id === currentUser.user_id || cu.user?.user_id === currentUser.user_id
			);
			if (isAssigned) return true;

			// Check if user is the department chair of someone assigned to the course
			if (isDeptChair) {
				const isDeptChairOfAssignee = courseUsers.some(
					(cu) => cu.user?.dept_chair?.user_id === currentUser.user_id
				);
				if (isDeptChairOfAssignee) return true;
			}

			return false;
		},
		[currentUser, isAdmin, isDeptChair]
	);

	/**
	 * Check if the current user can view a specific course.
	 * All authenticated users can view courses, but this determines
	 * if they have special access (edit rights) vs view-only.
	 */
	const canViewCourse = useCallback((): boolean => {
		// All authenticated users can view courses
		return true;
	}, []);

	/**
	 * Check if the current user can approve submissions for a course.
	 * - Admins can approve all submissions
	 * - Dept chairs can approve submissions for courses where they supervise an assignee
	 */
	const canApproveForCourse = useCallback(
		(courseUsers: CourseUserEntity[] | undefined): boolean => {
			if (!currentUser || !courseUsers) return false;

			// Admins can approve all
			if (isAdmin) return true;

			// Dept chairs can approve for their supervised users
			if (isDeptChair) {
				return courseUsers.some((cu) => cu.user?.dept_chair?.user_id === currentUser.user_id);
			}

			return false;
		},
		[currentUser, isAdmin, isDeptChair]
	);

	/**
	 * Check if the current user can delete a comment.
	 * - Admins can delete any comment
	 * - Users can only delete their own comments
	 */
	const canDeleteComment = useCallback(
		(commentUserId: string): boolean => {
			if (!currentUser) return false;
			if (isAdmin) return true;
			return commentUserId === currentUser.user_id;
		},
		[currentUser, isAdmin]
	);

	return {
		// Loading state
		isLoading,
		currentUser,

		// Role checks
		isAdmin,
		isDeptChair,
		isUser,
		userRole,

		// Feature permissions
		...permissions,

		// Course-specific permissions
		canEditCourse,
		canViewCourse,
		canApproveForCourse,

		// Comment permissions
		canDeleteComment,
	};
};

export type UsePermissionsReturn = ReturnType<typeof usePermissions>;
